/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Campaign info table configuration
 *
 * @author Yakov Gushchin
 * @version 1.12.0
 * @since 1.12.0
 */

import { ITableColumnUtilProps } from '../../../../components/Table/TableColumnUtil';
import { ColumnFieldType, EColumnCell, EColumnFilter } from '../../../../components/Table/types/columnTypes';
import { TableNameType } from '../../../../components/Table/types/tableTypes';
import {
    API_CAMPAIGN_ACCOUNT_INFO_BASKET,
    API_CAMPAIGN_INFO_ACCOUNT,
    API_CAMPAIGN_INFO_ACCOUNT_LIST,
    API_CAMPAIGN_INFO_BASKET,
    API_CAMPAIGN_INFO_SUBSCRIBER,
    API_CAMPAIGN_INFO_SUBSCRIBER_LIST,
    API_CAMPAIGN_SUBSCRIBER_INFO_BASKET,
} from '../../../../modules/app/Api';
import { ICommonTranslation, ICorporateTranslation } from '../../../../modules/internationalization/types';
import { IPageCampaignInfoTableTranslation } from '../../../../modules/internationalization/types/pages/campaignInfo';
import { ECampaignInfoType } from '../../types/campaignInfoTypes';

/*
* -------------------------------------------
*            Table Field
* -------------------------------------------
*/


export enum ECampaignInfoTableSubscriberField {
    account = 'accountNumber',
    msisdn = 'msisdn',
    name = 'name',
}

export enum ECampaignInfoTableAccountField {
    name = 'accountName',
    number = 'number',
}


/*
* -------------------------------------------
*          Table Column Names
* -------------------------------------------
*/

const TableAccountColumnNames: ECampaignInfoTableAccountField[] = [
    ECampaignInfoTableAccountField.name,
    ECampaignInfoTableAccountField.number,
];

const TableSubscriberColumnNames: ECampaignInfoTableSubscriberField[] = [
    ECampaignInfoTableSubscriberField.account,
    ECampaignInfoTableSubscriberField.msisdn,
    ECampaignInfoTableSubscriberField.name,
];

export type TCampaignInfoTableAccountData = { [Field in ECampaignInfoTableAccountField]: object };
export type TCampaignInfoTableSubscriberData = { [Field in ECampaignInfoTableSubscriberField]: object };


/*
* -------------------------------------------
*          Table Cell Config
* -------------------------------------------
*/


const TableAccountCellConfig: { [Field in ECampaignInfoTableAccountField]: number } = {
    [ECampaignInfoTableAccountField.name]: 240,
    [ECampaignInfoTableAccountField.number]: 240,
};

const TableSubscriberCellConfig: { [Field in ECampaignInfoTableSubscriberField]: number } = {
    [ECampaignInfoTableSubscriberField.account]: 150,
    [ECampaignInfoTableSubscriberField.msisdn]: 300,
    [ECampaignInfoTableSubscriberField.name]: 240,
};

export const getCampaignInfoTableAccountColumnFilterType = (name: string): string => {
    switch (name) {
        case ECampaignInfoTableAccountField.name:
        case ECampaignInfoTableAccountField.number:
            return EColumnFilter.filterMultiSelect;
        default:
            return '';
    }
};

export const getCampaignInfoTableSubscriberColumnFilterType = (name: string): string => {
    switch (name) {
        case ECampaignInfoTableSubscriberField.account:
        case ECampaignInfoTableSubscriberField.msisdn:
        case ECampaignInfoTableSubscriberField.name:
            return EColumnFilter.filterMultiSelect;
        default:
            return '';
    }
};

const getTableAccountColumnCellType = (name: ECampaignInfoTableAccountField): string => {
    switch (name) {
        case ECampaignInfoTableAccountField.number:
            return EColumnCell.linkCampaignAccount;
        case ECampaignInfoTableAccountField.name:
        default:
            return EColumnCell.text;
    }
};

const getTableSubscriberColumnCellType = (name: ECampaignInfoTableSubscriberField): string => {
    switch (name) {
        case ECampaignInfoTableSubscriberField.account:
            return EColumnCell.linkCampaignAccountNumber;
        case ECampaignInfoTableSubscriberField.msisdn:
            return EColumnCell.linkMobileCampaign;
        case ECampaignInfoTableSubscriberField.name:
        default:
            return EColumnCell.text;
    }
};

/*
* -------------------------------------------
*          Table Column Utils
* -------------------------------------------
*/

export type TTableTranslation = ICommonTranslation & ICorporateTranslation & IPageCampaignInfoTableTranslation;

type ITableStaticColumnUtil<T extends ColumnFieldType> =
    Pick<ITableColumnUtilProps<T, TTableTranslation>, 'cellConfig' | 'getColumnCellType' | 'getColumnFilterType' | 'columnNames'>;

export const CampaignInfoTableSubscriberColumnUtil: ITableStaticColumnUtil<ECampaignInfoTableSubscriberField> = {
    cellConfig: TableSubscriberCellConfig,
    getColumnCellType: getTableSubscriberColumnCellType,
    getColumnFilterType: getCampaignInfoTableSubscriberColumnFilterType,
    columnNames: TableSubscriberColumnNames,
};

export const CampaignInfoTableAccountColumnUtil: ITableStaticColumnUtil<ECampaignInfoTableAccountField> = {
    cellConfig: TableAccountCellConfig,
    getColumnCellType: getTableAccountColumnCellType,
    getColumnFilterType: getCampaignInfoTableAccountColumnFilterType,
    columnNames: TableAccountColumnNames,
};

export const mapTableNameToColumnUtils: { [key: string]: any } = {
    campaignInfoAccount: CampaignInfoTableAccountColumnUtil,
    campaignInfoSubscriber: CampaignInfoTableSubscriberColumnUtil,
};

export const mapCampaignInfoTypeToTableName:
    { [key: string]: TableNameType.campaignInfoAccount | TableNameType.campaignInfoSubscriber } = {
    [ECampaignInfoType.account]: TableNameType.campaignInfoAccount,
    [ECampaignInfoType.subscriber]: TableNameType.campaignInfoSubscriber,
};

export const mapCampaignInfoTypeToTableApiPath: { [key: string]: string } = {
    [ECampaignInfoType.account]: API_CAMPAIGN_INFO_ACCOUNT,
    [ECampaignInfoType.subscriber]: API_CAMPAIGN_INFO_SUBSCRIBER,
};

export const mapCampaignInfoTypeToTableListApiPath: { [key: string]: string } = {
    [ECampaignInfoType.account]: API_CAMPAIGN_INFO_ACCOUNT_LIST,
    [ECampaignInfoType.subscriber]: API_CAMPAIGN_INFO_SUBSCRIBER_LIST,
};

export const mapCampaignInfoTypeToTableBasketApiPath: { [key: string]: string } = {
    [ECampaignInfoType.account]: API_CAMPAIGN_INFO_BASKET,
    [ECampaignInfoType.subscriber]: API_CAMPAIGN_INFO_BASKET,
};

export const mapCampaignImprovedInfoTypeToTableBasketApiPath: { [key: string]: string } = {
    [ECampaignInfoType.account]: API_CAMPAIGN_ACCOUNT_INFO_BASKET,
    [ECampaignInfoType.subscriber]: API_CAMPAIGN_SUBSCRIBER_INFO_BASKET,
};

export type ICampaignInfoTableColumnUtilProps<T extends ColumnFieldType> =
    Pick<ITableColumnUtilProps<T, TTableTranslation>, 'componentsFeature' | 'fields' | 'isTableResizeEnabled' | 'isDisableSort'>
    & { translation: TTableTranslation };


